import React from 'react';
import ReactMarkdown from 'react-markdown';
import { resolveImageUrl } from '../lib/api';

const components = {
  h1: ({ children }) => <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4">{children}</h2>,
  h2: ({ children }) => <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">{children}</h2>,
  h3: ({ children }) => <h3 className="text-xl font-semibold text-[#8B2C3A] mt-8 mb-3">{children}</h3>,
  h4: ({ children }) => <h4 className="text-lg font-semibold text-gray-900 mt-6 mb-2">{children}</h4>,
  p: ({ children }) => <p className="text-gray-700 leading-relaxed mb-5 text-base md:text-lg">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-6 mb-5 space-y-2 text-gray-700 marker:text-[#8B2C3A]">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 mb-5 space-y-2 text-gray-700 marker:font-semibold marker:text-[#8B2C3A]">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  a: ({ href, children }) => <a href={href} className="text-[#8B2C3A] underline underline-offset-2 hover:text-[#6B1C2A]" target={href?.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer">{children}</a>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-[#D4AF37] bg-[#D4AF37]/5 pl-5 py-3 my-6 italic text-gray-700">{children}</blockquote>,
  img: ({ src, alt }) => <img src={resolveImageUrl(src)} alt={alt || ''} loading="lazy" className="rounded-xl shadow-md my-6 w-full object-cover" />,
  hr: () => <hr className="my-10 border-gray-200" />,
  code: ({ children }) => <code className="bg-gray-100 text-[#8B2C3A] px-1.5 py-0.5 rounded text-sm">{children}</code>,
};

const TABLE_SPLIT = /^\|(.+)\|\s*$/;

const parseTable = (block) => {
  const rows = block.trim().split('\n').filter(l => TABLE_SPLIT.test(l));
  if (rows.length < 2) return null;
  const cells = (line) => line.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
  const header = cells(rows[0]);
  const body = rows.slice(2).map(cells);
  return { header, body };
};

const inline = { p: ({ children }) => <>{children}</>, strong: components.strong, code: components.code, a: components.a };
const Cell = ({ text }) => <ReactMarkdown components={inline}>{text}</ReactMarkdown>;

const Table = ({ header, body }) => (
  <div className="overflow-x-auto my-6 rounded-xl border border-gray-200">
    <table className="min-w-full text-sm">
      <thead className="bg-[#8B2C3A] text-white">
        <tr>{header.map((h, i) => <th key={i} className="px-4 py-3 text-left font-semibold"><Cell text={h} /></th>)}</tr>
      </thead>
      <tbody>
        {body.map((row, r) => (
          <tr key={r} className={r % 2 ? 'bg-gray-50' : 'bg-white'}>
            {row.map((c, i) => <td key={i} className="px-4 py-3 text-gray-700 align-top"><Cell text={c} /></td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const MarkdownContent = ({ content }) => {
  const blocks = (content || '').split(/\n{2,}/);
  return (
    <div data-testid="markdown-content">
      {blocks.map((block, i) => {
        const table = block.trim().startsWith('|') ? parseTable(block) : null;
        return table
          ? <Table key={i} {...table} />
          : <ReactMarkdown key={i} components={components}>{block}</ReactMarkdown>;
      })}
    </div>
  );
};
